import { useEffect, useState, useCallback, RefObject } from 'react';
import useLayoutSize from '@/hooks/useLayoutSize';
import { LayoutType } from '@/constant/layout';

interface UseFullScreenOptions {
  onEnter?: () => void;
  onExit?: () => void;
}

interface UseFullScreenReturn {
  isFullScreen: boolean;
  toggleFullScreen: () => void;
  enterFullScreen: () => void;
  exitFullScreen: () => void;
}

const CSS_FULLSCREEN_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'fixed',
  top: '-20px',
  left: '0',
  right: '0',
  bottom: '0',
  zIndex: '100',
};

export const useFullScreen = (
  elementRef: RefObject<HTMLElement | null>,
  options: UseFullScreenOptions = {},
): UseFullScreenReturn => {
  const { isMobile } = useLayoutSize(LayoutType.DEFAULT);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const { onEnter, onExit } = options;

  const enterFullScreen = useCallback(() => {
    const element = elementRef.current as any;
    if (!element) return;

    if (isMobile) {
      Object.assign(element.style, CSS_FULLSCREEN_STYLE);
      setIsFullScreen(true);
      onEnter?.();
      return;
    }

    const requestFullscreen =
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.msRequestFullscreen;

    if (requestFullscreen) {
      requestFullscreen.call(element);
      onEnter?.();
    }
  }, [isMobile, elementRef, onEnter]);

  const exitFullScreen = useCallback(() => {
    const element = elementRef.current;
    if (!element) return;

    if (isMobile) {
      (
        Object.keys(CSS_FULLSCREEN_STYLE) as (keyof CSSStyleDeclaration)[]
      ).forEach((key) => {
        (element.style as any)[key] = '';
      });
      setIsFullScreen(false);
      onExit?.();
      return;
    }

    if (document.fullscreenElement || isFullScreen) {
      document.exitFullscreen();
      onExit?.();
    }
  }, [isFullScreen, elementRef, onExit]);

  const toggleFullScreen = useCallback(() => {
    if (document.fullscreenElement || isFullScreen) {
      exitFullScreen();
    } else {
      enterFullScreen();
    }
  }, [isFullScreen, enterFullScreen, exitFullScreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener(
        'msfullscreenchange',
        handleFullscreenChange,
      );
    };
  }, []);

  return {
    isFullScreen,
    toggleFullScreen,
    enterFullScreen,
    exitFullScreen,
  };
};
